<template>
  <v-dialog value="true" persistent max-width="630">
    <v-form ref="emailForm" v-model="localModalFormValid">
      <v-card class="EmailParentCarerDialog pa-4">
        <v-btn
          @click="close"
          class="ma-0 primary--text email-close-btn"
          right
          absolute
          fab
          small
          color="white"
          depressed
          aria-label="Close email parent/carer dialog"
          id="btn-close-email-parent-carer"
        >
          <v-icon>close</v-icon>
        </v-btn>

        <v-container
          class="px-8 my-5"
          v-if="emailParentCarerStatus === 'pending'"
        >
          <v-row justify="start" align="center">
            <h1>
              <span class="email-title-highlight">Email Parent/Carer</span> for
              {{ studentName }}?
            </h1>
          </v-row>
          <template v-if="parentEmail">
            <v-row justify="end" class="mt-5">
              <p>
                An email will be sent to parent/carer
                <strong>{{ parentName }}</strong> to advise about the
                application status.
              </p>
            </v-row>
            <v-row justify="start" class="mt-5">
              <p>
                The email will be sent to
                <strong>{{ parentEmail }}</strong>
              </p>
            </v-row>
            <v-row justify="start" class="mt-5">
              <p>
                This enrolment is related to a support class placement offer.
                OES notifications to families should be held until confirmation
                is received that the parent/carer is aware of this offer. Please
                check with the principal at your school before proceeding.
              </p>
            </v-row>
            <v-row justify="end">
              <v-card>
                <OesCheckbox
                  v-model="confirmSendEmail"
                  class="mr-1"
                  data-testid="confirm-send-email-checkbox"
                >
                  <template #label>
                    <p>
                      I confirm parent/carer is aware of this offer
                      <strong>{{ parentName }}</strong> about this application.
                      <strong
                        ><a
                          class="d-block mt-5"
                          @click.stop
                          target="_blank"
                          href="https://education.nsw.gov.au/platoapps/online-enrolment-system/online-enrolment-guides"
                          >View the email template</a
                        ></strong
                      >
                    </p>
                  </template>
                </OesCheckbox>
              </v-card>
            </v-row>
            <v-row justify="end" class="mt-5">
              <v-btn
                color="primary email-btn"
                @click="sendEmail"
                :disabled="!confirmSendEmail"
                data-testid="confirm-send-email"
              >
                Send Email
              </v-btn>
            </v-row>
          </template>
          <template v-else>
            <v-row justify="end" class="mt-5">
              <v-alert
                type="warning"
                outlined
                prominent
                class="ma-0"
                data-testid="no-email-warning"
              >
                <v-row align="center">
                  <v-col class="grow">
                    <strong>Email address not found</strong>
                    <p class="mb-0 mt-2">
                      No email address could be found for parent/carer
                      <strong>{{ parentName }}</strong
                      >. The email will need to be sent manually.
                    </p>
                  </v-col>
                </v-row>
              </v-alert>
            </v-row>
            <v-row justify="end" class="mt-5">
              <v-btn
                color="primary"
                @click="close"
                data-testid="close-no-email-btn"
              >
                Close
              </v-btn>
            </v-row>
          </template>
        </v-container>

        <v-container
          class="pl-5 pr-5 pb-0 pt-0"
          v-if="emailParentCarerStatus === 'sent'"
        >
          <v-row justify="start" align="center">
            <v-col cols="1" class="align-center justify-center d-flex pa-0">
              <v-avatar color="success" size="40">
                <v-icon color="white" size="34">mdi-email-check</v-icon>
              </v-avatar>
            </v-col>
            <v-col cols="11">
              <h1>Email sent successfully</h1>
            </v-col>
          </v-row>
          <v-row justify="end">
            <v-col cols="11">
              <p>
                The email has been successfully sent to
                <strong>{{ parentName }}</strong
                >.
              </p>
            </v-col>
          </v-row>
          <v-row justify="end">
            <v-btn color="primary" @click="backToApplications"
              >Back to applications</v-btn
            >
          </v-row>
        </v-container>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import OesCheckbox from '@/components/app/OesCheckbox'

export default {
  name: 'EmailParentCarerDialog',
  components: {
    OesCheckbox
  },
  data() {
    return {
      confirmSendEmail: false,
      localModalFormValid: true
    }
  },
  computed: {
    application() {
      return this.$store.state.application
    },
    emailParentCarerStatus() {
      return this.$store.state.emailParentCarerStatus
    },
    studentName() {
      const firstName = this.application?.student?.firstName || ''
      const familyName = this.application?.student?.familyName || ''

      if (firstName || familyName) {
        return (firstName + ' ' + familyName).trim()
      }
      return 'this student'
    },
    parentName() {
      const parent = this.application?.parentCarers?.find(
        (pc) => pc.isEnrolmentOwner
      )

      return parent
        ? `${parent.parentCarerGivenName} ${parent.parentCarerFamilyName}`
        : ''
    },
    parentEmail() {
      // Get all enrolment owners
      const enrolmentOwners =
        this.application?.parentCarers?.filter((pc) => pc.isEnrolmentOwner) ||
        []

      // Sort by contactOrder (ascending) and take the first one
      const parent = enrolmentOwners.sort(
        (a, b) => (a.contactOrder || 999) - (b.contactOrder || 999)
      )[0]

      if (!parent) return ''

      const contactDetails = parent?.contactDetails || []
      const emailDetail = contactDetails.find(
        (cd) => cd.contactType === 'EMAIL'
      )
      const email = emailDetail ? emailDetail.contactValue : ''
      return email ? email : ''
    }
  },
  methods: {
    close() {
      this.$store.dispatch('set', ['openEmailParentCarerModal', false])
      this.$store.dispatch('set', ['emailParentCarerStatus', null])
      this.confirmSendEmail = false
    },
    backToApplications() {
      this.close()
      this.$router.push('/')
    },
    async sendEmail() {
      const AWAITING_STATUS_ID = 8

      // Capture schoolCode and arxId before status update (which reloads the application)
      const schoolCode = this.application.schoolCode
      const arxId = this.application.arxId

      try {
        // Update application status to Awaiting
        await this.$store.dispatch('saveCoreApplicationStatus', {
          applicationStatusId: AWAITING_STATUS_ID,
          skipReload: true
        })

        // Send email to parent/carer
        await this.$store.dispatch('sendEmailToParentCarer', {
          schoolCode,
          arxId
        })

        // Update UI state to show success message
        this.$store.dispatch('set', ['emailParentCarerStatus', 'sent'])
      } catch (error) {
        // Error handling is done in the action itself via genericApiError
        // Close the modal on error
        this.close()
      }
    }
  }
}
</script>

<style scoped lang="scss">
.EmailParentCarerDialog {
  h1 {
    color: $color-primary;
    width: 90%;

    .email-title-highlight {
      color: $color-primary;
    }
  }
  .v-card {
    background: $grey-light-10;
    box-shadow: none;
    padding-left: 20px;

    ::v-deep .v-input__slot {
      align-items: initial;
    }

    ::v-deep .v-input--selection-controls__input .v-icon {
      color: $color-btn-disabled-text-default;
    }

    ::v-deep .v-input--selection-controls .v-input__slot > .v-label {
      color: $grey-dark;
    }
  }

  button.v-btn.email-close-btn {
    border: none;
    &:focus {
      box-shadow: 0 0 0 2px $color-focus !important;
    }
  }
  button.v-btn.email-btn {
    border: none;
    &:focus {
      box-shadow: 0 0 0 2px $color-focus !important;
    }
  }
}
</style>
